import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Page, Pageable} from 'src/app/model/rest/base';
import {
  ONE_SEARCH_TYPES,
  OneSearchAnyEntry,
  OneSearchGroupEntry,
  OneSearchManagerEntry,
  OneSearchPersonEntry,
  OneSearchStudentEntry,
  OneSearchTeacherEntry,
  OneSearchType
} from 'src/app/model/v3/one-search';
import {AdminRestService} from 'src/app/services/rest/admin-rest.service';
import {AuthServiceProvider} from 'src/app/services/col2/auth.service';
import {FunctionalAccessService} from 'src/app/services/functional-access.service';
import {of, Subject} from 'rxjs';
import {catchError, debounceTime, map, switchMap, takeUntil, tap} from 'rxjs/operators';

interface DashboardEntryRow {
  entry: OneSearchAnyEntry;
  typeLabel: string;
  name: string;
  id: number | null;
  schoolName: string;
  status: string;
  context: string;
  canOpen: boolean;
}

@Component({
    selector: 'app-admin-dashboard',
    templateUrl: './admin-dashboard.component.html',
    styleUrls: ['./admin-dashboard.component.scss'],
    standalone: false
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  readonly sortableColumns = ['type', 'name', 'school', 'status', 'context'] as const;
  readonly oneSearchTypes = ONE_SEARCH_TYPES;
  readonly defaultPageSize = 5;

  searchTerm: string;
  selectedType: OneSearchType | 'all' = 'all';
  sortColumn: (typeof this.sortableColumns)[number] | null = null;
  sortDirection: 'ASC' | 'DESC' = 'ASC';

  pageablePeople = new Pageable(0, this.defaultPageSize, []);
  searchedPage: Page<OneSearchAnyEntry> = Page.empty<OneSearchAnyEntry>();
  searchedRows: DashboardEntryRow[] = [];

  private searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private adminRest: AdminRestService,
    private authProvider: AuthServiceProvider,
    private functionalAccessService: FunctionalAccessService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        map(query => (query || '').trim()),
        debounceTime(350),
        switchMap(normalizedQuery => {
          if (!normalizedQuery || normalizedQuery.length < 3) {
            return of(null);
          }
          return this.adminRest.searchDashboardTable(
            normalizedQuery,
            this.pageablePeople,
            this.resolveTypesFilter()
          ).pipe(
            catchError(() => of(null))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (!result) {
          this.resetSearchResults();
          return;
        }
        this.searchedPage = result;
        this.searchedRows = this.toRows(result.content || []);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchTerm$.complete();
  }

  searchUpdated(resetPage: boolean = true) {
    if (resetPage) {
      this.pageablePeople = this.pageablePeople.first();
    }
    this.searchTerm$.next(this.searchTerm || '');
  }

  onTypeChanged() {
    this.searchUpdated(true);
  }

  onSortChanged(column: (typeof this.sortableColumns)[number]) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'ASC';
    }

    const sortField = this.toSortField(column);
    this.pageablePeople = new Pageable(0, this.pageablePeople.size, [`${sortField},${this.sortDirection}`]);
    this.searchUpdated(false);
  }

  getSortMarker(column: (typeof this.sortableColumns)[number]): string {
    if (this.sortColumn !== column) return '';
    return this.sortDirection === 'ASC' ? ' ↑' : ' ↓';
  }

  onPageChanged(pageable: Pageable) {
    this.pageablePeople = pageable;
    this.searchUpdated(false);
  }

  resetSearchForm() {
    this.searchTerm = '';
    this.selectedType = 'all';
    this.sortColumn = null;
    this.sortDirection = 'ASC';
    this.pageablePeople = new Pageable(0, this.defaultPageSize, []);
    this.resetSearchResults();
  }

  highlightText(value: string | null | undefined): string {
    const text = value || '-';
    const query = (this.searchTerm || '').trim();
    const safeText = this.escapeHtml(text);
    if (!query) return safeText;

    const escapedQuery = this.escapeRegExp(query);
    if (!escapedQuery) return safeText;
    return safeText.replace(new RegExp(`(${escapedQuery})`, 'ig'), '<mark>$1</mark>');
  }

  openEntry(entry: OneSearchAnyEntry): void {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.student: {
        const schoolId = (entry as OneSearchStudentEntry)?.school?.id;
        const roleId = (entry as OneSearchStudentEntry)?.id;
        if (!schoolId || !roleId) return;
        this.logInAsManagerAndNavigate(schoolId, `student/${roleId}`);
        return;
      }
      case ONE_SEARCH_TYPES.teacher: {
        const schoolId = (entry as OneSearchTeacherEntry)?.school?.id;
        const roleId = (entry as OneSearchTeacherEntry)?.id;
        if (!schoolId || !roleId) return;
        this.logInAsManagerAndNavigate(schoolId, `teacher/${roleId}`);
        return;
      }
      case ONE_SEARCH_TYPES.school: {
        const schoolId = (entry as any)?.id;
        if (!schoolId) return;
        this.router.navigate(['admin', 'school', schoolId, 'dashboard']).then();
        return;
      }
      case ONE_SEARCH_TYPES.group: {
        const groupId = (entry as OneSearchGroupEntry)?.id;
        if (!groupId) return;
        this.router.navigate(['admin', 'groups', groupId]).then();
        return;
      }
      default:
        return;
    }
  }

  private resetSearchResults() {
    this.searchedPage = Page.empty<OneSearchAnyEntry>();
    this.searchedRows = [];
  }

  private toRows(entries: OneSearchAnyEntry[]): DashboardEntryRow[] {
    return entries.map(entry => ({
      entry,
      typeLabel: this.entryTypeLabel(entry),
      name: this.entryName(entry),
      id: (entry as any)?.id ?? null,
      schoolName: this.entrySchoolName(entry),
      status: this.entryStatus(entry),
      context: this.entryContext(entry),
      canOpen: this.canOpen(entry),
    }));
  }

  private entryTypeLabel(entry: OneSearchAnyEntry): string {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.school: return 'School';
      case ONE_SEARCH_TYPES.person: return 'Person';
      case ONE_SEARCH_TYPES.student: return 'Student';
      case ONE_SEARCH_TYPES.teacher: return 'Teacher';
      case ONE_SEARCH_TYPES.manager: return 'Manager';
      case ONE_SEARCH_TYPES.group: return 'Group';
      default: return 'Unknown';
    }
  }

  private entryName(entry: OneSearchAnyEntry): string {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.school:
        return (entry as any)?.details?.name || '-';
      case ONE_SEARCH_TYPES.person:
        return this.fullName((entry as OneSearchPersonEntry)?.details?.name, (entry as OneSearchPersonEntry)?.details?.surname);
      case ONE_SEARCH_TYPES.student:
        return this.fullName((entry as OneSearchStudentEntry)?.person?.details?.name, (entry as OneSearchStudentEntry)?.person?.details?.surname);
      case ONE_SEARCH_TYPES.teacher:
        return this.fullName((entry as OneSearchTeacherEntry)?.person?.details?.name, (entry as OneSearchTeacherEntry)?.person?.details?.surname);
      case ONE_SEARCH_TYPES.manager:
        return this.fullName((entry as OneSearchManagerEntry)?.person?.details?.name, (entry as OneSearchManagerEntry)?.person?.details?.surname);
      case ONE_SEARCH_TYPES.group:
        return (entry as OneSearchGroupEntry)?.name || '-';
      default:
        return '-';
    }
  }

  private entrySchoolName(entry: OneSearchAnyEntry): string {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.school:
        return (entry as any)?.details?.name || '-';
      case ONE_SEARCH_TYPES.student:
        return (entry as OneSearchStudentEntry)?.school?.details?.name || '-';
      case ONE_SEARCH_TYPES.teacher:
        return (entry as OneSearchTeacherEntry)?.school?.details?.name || '-';
      case ONE_SEARCH_TYPES.manager:
        return (entry as OneSearchManagerEntry)?.school?.details?.name || '-';
      case ONE_SEARCH_TYPES.group:
        return (entry as OneSearchGroupEntry)?.school?.details?.name || '-';
      default:
        return '-';
    }
  }

  private entryStatus(entry: OneSearchAnyEntry): string {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.school:
        return (entry as any)?.status || '-';
      case ONE_SEARCH_TYPES.student:
        return (entry as OneSearchStudentEntry)?.status || '-';
      case ONE_SEARCH_TYPES.teacher:
        return (entry as OneSearchTeacherEntry)?.status || '-';
      case ONE_SEARCH_TYPES.group:
        return (entry as OneSearchGroupEntry)?.status || '-';
      default:
        return '-';
    }
  }

  private entryContext(entry: OneSearchAnyEntry): string {
    switch (entry?.['@type']) {
      case ONE_SEARCH_TYPES.person:
        return (entry as OneSearchPersonEntry)?.registrationEmail || '-';
      case ONE_SEARCH_TYPES.student:
        return (entry as OneSearchStudentEntry)?.person?.registrationEmail || '-';
      case ONE_SEARCH_TYPES.teacher:
        return (entry as OneSearchTeacherEntry)?.person?.registrationEmail || '-';
      case ONE_SEARCH_TYPES.manager:
        return (entry as OneSearchManagerEntry)?.person?.registrationEmail || '-';
      case ONE_SEARCH_TYPES.group:
        return this.getGroupMeta(entry as OneSearchGroupEntry);
      case ONE_SEARCH_TYPES.school:
        return (entry as any)?.expDate ? `Exp: ${this.formatDateFriendly((entry as any).expDate)}` : '-';
      default:
        return '-';
    }
  }

  private canOpen(entry: OneSearchAnyEntry): boolean {
    return entry?.['@type'] === ONE_SEARCH_TYPES.student
      || entry?.['@type'] === ONE_SEARCH_TYPES.teacher
      || entry?.['@type'] === ONE_SEARCH_TYPES.school
      || entry?.['@type'] === ONE_SEARCH_TYPES.group;
  }

  private logInAsManagerAndNavigate(schoolId: number, redirectPath: string): void {
    this.authProvider
      .get()
      .pipe(
        tap((auth) => auth.logInAsManager(schoolId, redirectPath)),
        tap(() => this.functionalAccessService.initialize())
      )
      .subscribe();
  }

  private resolveTypesFilter(): OneSearchType[] | undefined {
    if (!this.selectedType || this.selectedType === 'all') return undefined;
    return [this.selectedType];
  }

  private toSortField(column: (typeof this.sortableColumns)[number]): string {
    switch (column) {
      case 'type': return 'type';
      case 'name': return 'name';
      case 'school': return 'school';
      case 'status': return 'status';
      case 'context': return 'context';
      default: return 'name';
    }
  }

  private getGroupMeta(entry: OneSearchGroupEntry): string {
    const members = entry?.membersCount;
    if (members == null) return '-';
    return `Members: ${members}`;
  }

  private formatDateFriendly(value: string | null | undefined): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private fullName(name: string | null | undefined, surname: string | null | undefined): string {
    return `${name || ''} ${surname || ''}`.trim() || '-';
  }
}
